<script lang="ts" setup>
defineOptions({ name: 'ContactPage' })

import { reactive } from 'vue'
import { Icon } from '@/components/Icon'
import { ElMessage } from 'element-plus'
import { usePortalState } from '../usePortalState'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'

const { showTrialModal } = usePortalState()

const form = reactive({
  name: '',
  phone: '',
  email: '',
  message: ''
})

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    ElMessage.success('已复制到剪贴板')
  } catch {
    // 降级方案
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    ElMessage.success('已复制到剪贴板')
  }
}

const contactCards = [
  {
    icon: 'lucide:phone',
    iconColor: '#0066FF',
    iconBg: '#EBF5FF',
    label: '客服热线',
    value: '400-646-5288',
    valueSize: '18px',
    action: '复制电话',
    actionFn: () => copyToClipboard('400-646-5288')
  },
  {
    icon: 'lucide:mail',
    iconColor: '#00B96B',
    iconBg: '#ECFDF5',
    label: '邮箱',
    value: 'kefu@zjsai.com',
    valueSize: '16px',
    action: '复制邮箱',
    actionFn: () => copyToClipboard('kefu@zjsai.com')
  },
  {
    icon: 'lucide:briefcase',
    iconColor: '#FF9500',
    iconBg: '#FFFBEB',
    label: '商务合作',
    value: '18565827188',
    valueSize: '16px',
    action: '联系商务',
    actionFn: () => showTrialModal()
  },
  {
    icon: 'lucide:headphones',
    iconColor: '#7C3AED',
    iconBg: '#F3E8FF',
    label: '技术支持',
    value: '0755-888-99225',
    valueSize: '16px',
    action: '复制电话',
    actionFn: () => copyToClipboard('0755-888-99225')
  }
]

async function handleSubmit() {
  if (!form.name || !form.phone || !form.message) {
    ElMessage.warning('请填写姓名、联系电话和留言内容')
    return
  }
  try {
    await submitPortalForm({
      username: form.name,
      tel: form.phone,
      email: form.email,
      content: form.message,
      type: 'CONTACT'
    })
    ElMessage.success('留言已提交！我们会尽快联系您')
    Object.assign(form, { name: '', phone: '', email: '', message: '' })
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<template>
  <div class="contact-page">
    <div class="content-wrap">
      <h1 class="page-title">联系我们</h1>

      <!-- Contact Cards -->
      <div class="card-grid">
        <div v-for="card in contactCards" :key="card.label" class="contact-card card-hover">
          <div class="card-icon" :style="{ background: card.iconBg }">
            <Icon :icon="card.icon" :size="24" :style="{ color: card.iconColor }" />
          </div>
          <p class="card-label">{{ card.label }}</p>
          <p class="card-value" :style="{ fontSize: card.valueSize }">{{ card.value }}</p>
          <button class="card-action" :style="{ color: card.iconColor }" @click="card.actionFn()">
            {{ card.action }}
          </button>
        </div>
      </div>

      <!-- Map + Form -->
      <div class="map-form-grid">
        <div class="map-placeholder">
          <div class="map-bg" />
          <div class="map-content">
            <div class="map-pin-icon">
              <Icon icon="lucide:map-pin" :size="24" color="#fff" />
            </div>
            <p class="map-address-main">广东省深圳市宝安区</p>
            <p class="map-address-sub">金海路12号 汇潮科技大厦2605</p>
          </div>
        </div>

        <div class="form-card">
          <h3 class="form-title">在线留言</h3>
          <div class="form-fields">
            <input v-model="form.name" class="form-input" placeholder="您的姓名" />
            <input v-model="form.phone" class="form-input" type="tel" placeholder="联系电话" />
            <input v-model="form.email" class="form-input" type="email" placeholder="邮箱" />
            <textarea
              v-model="form.message"
              class="form-input form-textarea"
              placeholder="留言内容"
            />
            <button class="form-submit-btn" @click="handleSubmit">提交留言</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content-wrap {
  min-height: calc(100vh - 64px);
  padding: 48px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 32px;
  text-align: center;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 48px;
}

.contact-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.card-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
}

.card-value {
  font-size: 16px;
  font-weight: 700;
}

.card-action {
  margin-top: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
}

.map-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 48px;
}

.map-placeholder {
  background: #f3f4f6;
  border-radius: 12px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.map-bg {
  position: absolute;
  inset: 0;
  background: url('https://picsum.photos/seed/map/600/400') center / cover;
  opacity: 0.3;
}

.map-content {
  position: relative;
  z-index: 2;
  text-align: center;
}

.map-pin-icon {
  width: 48px;
  height: 48px;
  background: #0066ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  animation: map-pulse 2s infinite;
}

@keyframes map-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.map-address-main {
  font-weight: 600;
}

.map-address-sub {
  font-size: 13px;
  color: #6b7280;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 32px;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-input {
  height: 44px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #0066ff;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.form-textarea {
  height: 100px;
  padding: 12px 14px;
  resize: none;
}

.form-submit-btn {
  height: 44px;
  background: #0066ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #0044cc;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 102, 255, 0.3);
  }
}

.card-hover {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }
}

@media (max-width: 1024px) {
  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .card-grid {
    grid-template-columns: 1fr;
  }

  .map-form-grid {
    grid-template-columns: 1fr;
  }

  .map-placeholder {
    height: 280px;
  }
}
</style>
